// Composes resolveSupply per payment path. Spec §1 "resolveTransactionVat",
// dynamic VAT decision 1 (Freedomly is the principal on the invoice and light
// paths) and customer service fee decision 2 (the fee is Freedomly's sale to
// the customer).
const { resolveSupply, FREEDOMLY_AS_SELLER, FREEDOMLY_AS_BUYER, VALIDATION } = require('./resolve');

const PATH = {
  STRIPE: 'stripe',
  INVOICE: 'invoice',
  LIGHT_MAIN: 'light-main',
  LIGHT_CARD: 'light-card',
};
const HOLD = { VAT_VALIDATION_PENDING: 'VAT_VALIDATION_PENDING' };

const composeOnce = ({ path, seller, buyer }) => {
  const supplies = {};
  if (path === PATH.STRIPE) {
    supplies.core = resolveSupply(seller, buyer);
  } else if (path === PATH.INVOICE || path === PATH.LIGHT_MAIN) {
    supplies.providerSale = resolveSupply(seller, FREEDOMLY_AS_BUYER);
    supplies.customerSale = resolveSupply(FREEDOMLY_AS_SELLER, buyer);
  } else if (path === PATH.LIGHT_CARD) {
    supplies.customerSale = resolveSupply(FREEDOMLY_AS_SELLER, buyer);
  } else {
    throw new Error(`Unknown VAT path: ${path}`);
  }
  // The service fee is Freedomly's own sale to the customer on every path
  // (customer service fee spec 2026-10-05, decision 2): the customer's
  // location and status decide its VAT, never the freelancer's. Matrix B
  // (resolvePlatformFee) is no longer composed; v1 snapshots still carry it.
  supplies.serviceFee = resolveSupply(FREEDOMLY_AS_SELLER, buyer);
  const blocked = Object.keys(supplies)
    .map(k => supplies[k].blocked)
    .find(Boolean);
  return blocked ? { path, supplies, blocked } : { path, supplies };
};

// Helper: normalize a supply by stripping 'case' (only the outcome matters for hold).
// No object spread/rest in src/util/vat: Babel turns it into an ES-module
// helper import, which makes webpack treat this CommonJS file as an ES module
// and `module.exports = …` then throws "Cannot assign to read only property
// 'exports'" when the client bundle loads.
const stripCase = supply => {
  if (!supply) return supply;
  const rest = Object.assign({}, supply);
  delete rest.case;
  return rest;
};

// Decision 9: a PENDING VIES answer holds the transaction only when it
// matters — resolve as VERIFIED and as UNVERIFIED and compare.
// "Matters" = outcome differs in rate, label, noteKey, placeOfSupply, or blocked.
const outcomeDiffers = (res1, res2) => {
  const supplies1 = res1.supplies || {};
  const supplies2 = res2.supplies || {};
  const allKeys = new Set([...Object.keys(supplies1), ...Object.keys(supplies2)]);
  for (const key of allKeys) {
    const s1 = stripCase(supplies1[key]);
    const s2 = stripCase(supplies2[key]);
    if (JSON.stringify(s1) !== JSON.stringify(s2)) return true;
  }
  return res1.blocked !== res2.blocked;
};

const withStatus = (buyer, status) => Object.assign({}, buyer, { vatValidationStatus: status });

const resolveTransactionVat = ({ path, seller, buyer }) => {
  if (buyer && buyer.vatValidationStatus === VALIDATION.PENDING) {
    const asVerified = composeOnce({ path, seller, buyer: withStatus(buyer, VALIDATION.VERIFIED) });
    const asUnverified = composeOnce({
      path,
      seller,
      buyer: withStatus(buyer, VALIDATION.UNVERIFIED),
    });
    if (outcomeDiffers(asVerified, asUnverified)) {
      return { path, hold: HOLD.VAT_VALIDATION_PENDING };
    }
    return asUnverified;
  }
  return composeOnce({ path, seller, buyer });
};

module.exports = { PATH, HOLD, resolveTransactionVat };
